/**
 * Alpine handlers for the moderator deleted-accounts queue page.
 *
 * Two components cooperate:
 *  - modDeletedAccountsPageModal: shared input modal for the actions that
 *    take free-form text (block reason, clear-review note). Listens for
 *    `open-lifecycle-modal` events dispatched from row factories so the
 *    modal markup is rendered exactly once for the page.
 *  - modDeletedAccountRow: per-row buttons. Calls
 *    `POST /v1/deleted-accounts/{id}/{action}` via window.ApiClient and
 *    fades the row out on success.
 */

const REQUEST_TIMEOUT_MS = 30000;

function withTimeout(promise, controller) {
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    return promise.finally(() => clearTimeout(timeoutId));
}

function postFormUrlEncoded(endpoint, params) {
    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined || value === '') {
            return;
        }
        body.append(key, String(value));
    });

    const controller = new AbortController();
    return withTimeout(window.ApiClient.request(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        signal: controller.signal,
    }), controller);
}

function postEmpty(endpoint) {
    const controller = new AbortController();
    return withTimeout(window.ApiClient.request(endpoint, {
        method: 'POST',
        signal: controller.signal,
    }), controller);
}

function confirmAction(options) {
    return new Promise((resolve) => {
        if (!window.ConfirmModal || typeof window.ConfirmModal.show !== 'function') {
            resolve(window.confirm(options.message));
            return;
        }
        window.ConfirmModal.show({
            title: options.title,
            message: options.message,
            confirmText: options.confirmText || 'Confirm',
            cancelText: 'Cancel',
            type: options.type || 'warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

async function extractErrorMessage(response, fallback) {
    let message = fallback || ('Request failed (' + response.status + ').');
    try {
        const body = await response.json();
        if (body && body.error && body.error.message) {
            message = body.error.message;
        }
    } catch (_e) {
        // Body wasn't JSON; fall back to the constructed message.
    }
    return message;
}

export function modDeletedAccountsPageModal() {
    return {
        visible: false,
        loading: false,
        mode: '',
        userId: '',
        username: '',
        text: '',
        errorMessage: '',
        callback: null,

        init() {
            this.forceClose();
        },

        open(detail = {}) {
            if (!detail
                || !['block', 'clear-review'].includes(detail.mode)
                || !detail.userId
                || typeof detail.callback !== 'function'
            ) {
                this.forceClose();
                return;
            }

            this.mode = detail.mode || '';
            this.userId = detail.userId || '';
            this.username = detail.username || '';
            this.callback = detail.callback;
            this.text = '';
            this.errorMessage = '';
            this.visible = true;
        },

        reset() {
            this.loading = false;
            this.mode = '';
            this.userId = '';
            this.username = '';
            this.text = '';
            this.errorMessage = '';
            this.callback = null;
        },

        forceClose() {
            this.visible = false;
            this.reset();
        },

        close() {
            this.forceClose();
        },

        async submit() {
            if (!this.callback) {
                this.close();
                return;
            }
            if (this.mode === 'block' && this.text.trim() === '') {
                this.errorMessage = 'Reason is required.';
                return;
            }
            this.loading = true;
            this.errorMessage = '';
            try {
                // Callback returns { ok, error } so the modal can surface
                // server-side validation errors inline rather than letting
                // them land on the (still-hidden) row underneath.
                const result = await this.callback(this.text);
                if (result && result.ok) {
                    this.visible = false;
                    this.callback = null;
                } else if (result && result.error) {
                    this.errorMessage = result.error;
                }
            } catch (err) {
                this.errorMessage = (err && err.message) ? err.message : 'Network error';
            } finally {
                this.loading = false;
            }
        },
    };
}

export function modDeletedAccountRow(config = {}) {
    return {
        userId: String(config.userId || ''),
        username: String(config.username || ''),
        loading: false,
        removed: false,
        errorMessage: '',

        markRemoved() {
            this.removed = true;
        },

        async finalize() {
            const ok = await confirmAction({
                title: 'Finalize pending delete',
                message: `Finalize the pending delete for ${this.username}? The account will be deleted immediately rather than waiting for the cron.`,
                confirmText: 'Finalize',
                type: 'danger',
            });
            if (!ok) {
                return;
            }
            await this.runAction(() => postEmpty(`/deleted-accounts/${this.userId}/finalize`));
        },

        async cancel() {
            const ok = await confirmAction({
                title: 'Cancel pending delete',
                message: `Cancel the pending delete for ${this.username}? The account stays active.`,
                confirmText: 'Cancel pending',
                type: 'warning',
            });
            if (!ok) {
                return;
            }
            await this.runAction(() => postEmpty(`/deleted-accounts/${this.userId}/cancel`));
        },

        async unblock() {
            const ok = await confirmAction({
                title: 'Unblock reactivation',
                message: `Remove the moderator hold on reactivation for ${this.username}?`,
                confirmText: 'Unblock',
                type: 'warning',
            });
            if (!ok) {
                return;
            }
            await this.runAction(() => postEmpty(`/deleted-accounts/${this.userId}/unblock`));
        },

        openBlock() {
            this.errorMessage = '';
            this.$dispatch('open-lifecycle-modal', {
                mode: 'block',
                userId: this.userId,
                username: this.username,
                callback: (text) => this.runAction(() => postFormUrlEncoded(
                    `/deleted-accounts/${this.userId}/block`,
                    { reason: text },
                )),
            });
        },

        openClearReview() {
            this.errorMessage = '';
            this.$dispatch('open-lifecycle-modal', {
                mode: 'clear-review',
                userId: this.userId,
                username: this.username,
                callback: (text) => this.runAction(() => postFormUrlEncoded(
                    `/deleted-accounts/${this.userId}/clear-review`,
                    { note: text },
                )),
            });
        },

        async runAction(requestFn) {
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await requestFn();
                if (response.ok) {
                    this.markRemoved();
                    return { ok: true };
                }
                const error = await extractErrorMessage(
                    response,
                    response.status === 409 ? 'No matching pending state. Refresh to see the latest data.' : null,
                );
                this.errorMessage = error;
                return { ok: false, error };
            } catch (err) {
                const error = (err && err.name === 'AbortError')
                    ? 'Request timed out. Check the moderation log and retry.'
                    : ((err && err.message) ? err.message : 'Network error');
                this.errorMessage = error;
                return { ok: false, error };
            } finally {
                this.loading = false;
            }
        },
    };
}
